/**
 * Centralised scene-visibility gating.
 *
 * Scene groups normally toggle their own `visible` flag inside `show()` /
 * `hide()` — but when scenes change asynchronously (`round_change` arrives
 * mid-cinematic) stale meshes bleed through onto the next scene. This helper
 * authoritatively sets every scene group's visibility every time the
 * top-level scene changes, so the active scene always owns the stage alone.
 *
 * Pulling this out of `main.ts` keeps the entrypoint under the 500-line cap
 * the code-quality hook enforces.
 */

import type { Object3D } from 'three';
import type { LbRow } from '@sg/shared';
import type { SceneName } from '../state/types.js';

export interface SceneVisibilityHandles {
  board: { group: Object3D };
  leaderboard: { group: Object3D };
  podium: { group: Object3D; show: (rows: LbRow[]) => void; hide: () => void };
  roundTransition: { hide: () => void };
  /**
   * Permanent right-side leaderboard panel. Replaces the corner mini-LB
   * (removed at the same time this was introduced). Visible during all
   * gameplay scenes; hidden for the round_transition cinematic and for
   * meta-states (lobby, connecting, calibration, podium).
   */
  sideLeaderboard: { setVisible: (v: boolean) => void };
  lobby: { setVisible: (v: boolean) => void };
  instructions: { hideInstruction: () => void };
  connectionLost: { setState: (s: 'open') => void };
}

/** Scenes during which the dollar-value board grid should be on stage. */
function isBoardScene(s: SceneName): boolean {
  // poll_category and poll_clue_value are NOT included: full-screen DOM
  // overlays (ui/category-vote.ts + ui/value-vote.ts) own the stage during
  // voting. Showing the board underneath creates a "stacked cards on top
  // of tiles" visual mash-up.
  return (
    s === 'board' ||
    s === 'answer_reveal'
  );
}

/**
 * Apply visibility rules for one scene transition. Pulls the latest
 * leaderboard from `window.__sj_lastLb` so the podium can surface final
 * standings without threading state through main.ts.
 */
export function applySceneVisibility(next: SceneName, h: SceneVisibilityHandles): void {
  const boardVisible = isBoardScene(next);
  h.board.group.visible = boardVisible;
  h.lobby.setVisible(next === 'lobby');
  // Full-screen "current game leaderboard" card is reserved for the
  // round_transition cinematic. During regular gameplay scenes the permanent
  // right-side panel handles standings, so the big card would just duplicate
  // information and block the board.
  h.leaderboard.group.visible = next === 'round_transition';
  // Permanent side leaderboard: on for all gameplay phases, off for the
  // round_transition cinematic that already shows standings full-screen,
  // and off for meta scenes (lobby/connecting/calibration/podium) so those
  // cinematics own the stage alone. poll_category is INCLUDED — the
  // full-screen DOM category overlay has its own blur scrim that the side
  // panel doesn't sit under (panel is z=50 vs overlay z=80+).
  const gameplaySideScenes: SceneName[] = [
    'board',
    'poll_category',
    'poll_clue_value',
    'clue_zoom',
    'grace',
    'answer_reveal',
  ];
  h.sideLeaderboard.setVisible(gameplaySideScenes.includes(next));
  if (next === 'lobby' || next === 'connecting') h.instructions.hideInstruction();
  if (next !== 'connecting' && next !== 'lobby') h.connectionLost.setState('open');

  // Round-transition title auto-hides after ~5s but force-clear it as a
  // belt-and-braces guarantee when entering podium.
  if (next === 'podium') {
    h.roundTransition.hide();
  }

  if (next === 'podium') {
    const rows = (window as unknown as { __sj_lastLb?: LbRow[] }).__sj_lastLb ?? [];
    h.podium.show(rows);
  } else {
    h.podium.hide();
  }
}
